import { formIsCompleted } from "./form.js";
/**
 * Exhaustive over `LinkedFormReason`, so a new relationship cannot be added
 * without deciding how a redo travels along it. Exactly one role per reason, so
 * the scopes below can neither overlap nor leave a reason unclassified.
 */
const REDO_LINK_ROLE = {
    "supported-by": "inherited-basis",
    acknowledges: "basis-only",
    supports: "dependent",
    "acknowledged-by": "dependent",
};
// Spread rather than restated, so "everything inherited is also a basis" holds by
// construction: a role cannot be added to inheritance without joining basis too.
const INHERITANCE_ROLES = ["inherited-basis"];
const BASIS_ROLES = [...INHERITANCE_ROLES, "basis-only"];
const SCOPE_ROLES = {
    inheritance: INHERITANCE_ROLES,
    basis: BASIS_ROLES,
};
/** Whether a link with `reason` is one that `scope` follows. */
export function redoLinkInScope(scope, reason) {
    return SCOPE_ROLES[scope].includes(REDO_LINK_ROLE[reason]);
}
/**
 * Inherited states in least-resolved-first order: whichever appears first among
 * the contributions wins. `redo-requested` outranks `redo-in-progress` because
 * an outstanding request can still be answered, so it is the state the user must
 * act on first.
 */
const INHERITANCE_PRECEDENCE = [
    "redo-requested",
    "redo-in-progress",
];
/**
 * What a dependency in each redo state contributes to its downstream forms.
 *
 * `void` contributes `redo-in-progress`: a voided dependency cannot resolve
 * itself, so the downstream needs a redo of its own to relink, and
 * `redo-in-progress` is the state that permits one. A downstream is only ever
 * `void` through its own replacement being signed.
 *
 * Exhaustive over `RedoState`, so a new state cannot be added without deciding
 * what it propagates; and contributions are `InheritedRedoState`, so it cannot be
 * mapped to something the precedence order would silently ignore.
 */
const DEPENDENCY_CONTRIBUTION = {
    "redo-requested": "redo-requested",
    "redo-in-progress": "redo-in-progress",
    void: "redo-in-progress",
};
/**
 * The upstream forms `form` rests on, within the given scope.
 *
 * Includes the upstreams whose link was SEVERED when they entered redo: that
 * link is removed from `linkedForms` for the duration (and the record retained
 * after the upstream voids), so without them the very dependency that put this
 * form under redo would be invisible.
 */
export function redoBasisUpstreamIds(form, scope) {
    return ([
        ...new Set([
            ...form.linkedForms
                .filter((link) => redoLinkInScope(scope, link.reason))
                .map((link) => link.id),
            ...(form.redoSeveredUpstreamFormIds ?? []),
        ]),
    ]
        // This is a set of dependencies, not a sequence, and the order they arrive in
        // is not guaranteed. Sorting gives it one canonical order, so the same
        // dependencies always compare equal and nothing can come to rely on an order
        // that was only ever incidental.
        .sort());
}
/**
 * The redo state a downstream form inherits from the current state of ALL its
 * dependencies — the least-resolved contribution, or null when none of them is
 * under redo.
 *
 * Deriving rather than recording this is what makes the result independent of
 * the order in which dependencies entered or left redo. A dependency that is not
 * completed contributes nothing: it is not yet a basis, and a form's inability
 * to progress on an incomplete basis is a separate guard.
 */
export function redoInheritedState(dependencies) {
    const contributions = new Set(dependencies
        .filter(formIsCompleted)
        .flatMap((dependency) => dependency.redoState
        ? [DEPENDENCY_CONTRIBUTION[dependency.redoState]]
        : []));
    // Several dependencies commonly contribute at once, so more than one entry
    // matching is the ordinary case rather than an ambiguity: the first match is the
    // least-resolved of them, which is the one the user must act on. The result turns
    // only on WHICH states were contributed and on the order of the list below —
    // never on the order the dependencies came in.
    return (INHERITANCE_PRECEDENCE.find((state) => contributions.has(state)) ?? null);
}
/**
 * A form's redo state as everyone outside the redo lifecycle should see it: its
 * own recorded state if it has one, otherwise what it inherits.
 *
 * The form's own state always wins, so this can only ever ADD a state where
 * there was none — it never changes or removes one. An in-progress form inherits
 * nothing; it is blocked from completing by the valid-basis guard instead, which
 * gives a far more actionable message than a redo status would.
 */
export function effectiveRedoState(form, dependencies) {
    if (form.redoState) {
        return form.redoState;
    }
    return formIsCompleted(form) ? redoInheritedState(dependencies) : null;
}
/**
 * Whether every form a downstream rests on is still usable as a basis — i.e.
 * none is under redo or voided.
 *
 * Defined through `redoInheritedState` so the two can never disagree: a form
 * inherits a redo state precisely when its basis has stopped being valid.
 */
export function redoBasisIsValid(dependencies) {
    return redoInheritedState(dependencies) == null;
}
//# sourceMappingURL=redoDerivation.js.map